import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { Fluid } from 'primeng/fluid';
import { AuthService } from '@/app/services/auth.service';
import { CategoryService } from '@/app/services/category.service';
import { CategoryCurrencyForm } from '@/app/components/category-config/category-currency-form';
import { CategoryWithdrawForm } from '@/app/components/category-config/category-withdraw-form';
import { CategoryKycForm } from '@/app/components/category-config/category-kyc-form';
import { CategorySumsubForm } from '@/app/components/category-config/category-sumsub-form';
import { CategoryLegalForm } from '@/app/components/category-config/category-legal-form';
import { emptyLegalConfig } from '@/app/models/legal-documents';
import {
    Category,
    CategoryConfig,
    CategoryCurrencyConfig,
    CategoryKycConfig,
    CategoryLegalConfig,
    CategorySumsubConfig,
    CategoryType,
    CategoryUpdatePatch,
    CategoryWithdrawConfig,
} from '@/lib-backend';
import * as config from '@/lib-backend/methods/config';

/** Real (selectable) category types — Unknown is excluded from the UI. */
const SELECTABLE_TYPES: { label: string; value: CategoryType }[] = [
    { label: 'Skill-based games', value: CategoryType.SkillBasedGames },
    { label: 'Lucky-based games', value: CategoryType.LuckyBasedGames },
];

function defaultKyc(): CategoryKycConfig {
    return { RequireForDeposit: false, RequireForWithdrawal: false };
}

function defaultSumsub(): CategorySumsubConfig {
    return { LevelName: '' };
}

function defaultCurrency(): CategoryCurrencyConfig {
    return {
        CreditsPerDollar: { FreeCredits: 0, PaidCredits: 0 },
        FreeCurrencyType: 'default',
        PaidCurrencyType: 'default',
        InitialFunds: { FreeCredits: 0, PaidCredits: 0 },
        ShowFreeCurrency: true,
        ShowPaidCurrency: false,
    };
}

function defaultWithdraw(): CategoryWithdrawConfig {
    return {
        WithdrawEnabled: false,
        MinWithdrawAmountUsd: 0,
        WithdrawCreditsPerDollar: 0,
    };
}

@Component({
    selector: 'app-categories',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Dialog,
        InputText,
        Message,
        Select,
        Tabs,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        TableModule,
        Toast,
        Tooltip,
        Fluid,
        CategoryCurrencyForm,
        CategoryWithdrawForm,
        CategoryKycForm,
        CategorySumsubForm,
        CategoryLegalForm,
    ],
    providers: [MessageService],
    templateUrl: './categories.html',
})
export class Categories implements OnInit {
    protected readonly categoryService = inject(CategoryService);
    private readonly authService = inject(AuthService);
    private readonly messageService = inject(MessageService);

    protected readonly isGlobalAdmin = this.authService.isGlobalAdmin;

    protected readonly typeOptions = SELECTABLE_TYPES;
    protected readonly CategoryType = CategoryType;

    // ── Dialog state ─────────────────────────────────────────────────────────
    protected readonly dialogVisible = signal(false);
    /** null → add mode; otherwise edit mode. */
    protected readonly editing = signal<Category | null>(null);
    protected readonly saving = signal(false);
    protected readonly dialogTab = signal<string | number>('general');

    protected readonly mode = computed<'add' | 'edit'>(() =>
        this.editing() ? 'edit' : 'add',
    );
    protected readonly dialogTitle = computed(() =>
        this.mode() === 'add'
            ? 'Add Category'
            : `Edit — ${this.editing()!.Name}`,
    );

    // ── Form fields ──────────────────────────────────────────────────────────
    protected readonly id = signal('');
    protected readonly name = signal('');
    protected readonly type = signal<CategoryType>(
        CategoryType.SkillBasedGames,
    );

    protected readonly currency =
        signal<CategoryCurrencyConfig>(defaultCurrency());
    protected readonly kyc = signal<CategoryKycConfig>(defaultKyc());
    protected readonly sumsub = signal<CategorySumsubConfig>(defaultSumsub());
    protected readonly withdraw =
        signal<CategoryWithdrawConfig>(defaultWithdraw());
    protected readonly legal = signal<CategoryLegalConfig>(emptyLegalConfig());

    // ── Errors ───────────────────────────────────────────────────────────────
    protected readonly idError = signal<string | null>(null);
    protected readonly nameError = signal<string | null>(null);
    protected readonly generalError = signal<string | null>(null);

    protected readonly typeLabel = computed(() => {
        const t = this.type();
        return this.typeOptions.find((o) => o.value === t)?.label ?? t;
    });

    ngOnInit(): void {
        this.categoryService.loadCategories();
    }

    protected refresh(): void {
        this.categoryService.loadCategories();
    }

    protected typeLabelOf(t: CategoryType): string {
        return this.typeOptions.find((o) => o.value === t)?.label ?? t;
    }

    private clearErrors(): void {
        this.idError.set(null);
        this.nameError.set(null);
        this.generalError.set(null);
    }

    protected openCreate(): void {
        this.editing.set(null);
        this.id.set('');
        this.name.set('');
        this.type.set(CategoryType.SkillBasedGames);
        this.currency.set(defaultCurrency());
        this.kyc.set(defaultKyc());
        this.sumsub.set(defaultSumsub());
        this.withdraw.set(defaultWithdraw());
        this.legal.set(emptyLegalConfig());
        this.clearErrors();
        this.dialogTab.set('general');
        this.dialogVisible.set(true);
    }

    protected openEdit(category: Category): void {
        this.editing.set(category);
        this.id.set(category.CategoryId);
        this.name.set(category.Name);
        const cfg = category.Config;
        this.type.set(cfg.type);
        this.currency.set({ ...cfg.Currency });
        this.kyc.set({ ...cfg.Kyc });
        this.sumsub.set({ ...cfg.Sumsub });
        this.withdraw.set(
            cfg.type === CategoryType.SkillBasedGames
                ? { ...cfg.Withdraw }
                : defaultWithdraw(),
        );
        this.legal.set(cloneLegal(cfg.Legal));
        this.clearErrors();
        this.dialogTab.set('general');
        this.dialogVisible.set(true);
    }

    protected close(): void {
        this.dialogVisible.set(false);
    }

    /** Assemble a CategoryConfig of the given type from the current form signals. */
    private buildConfig(type: CategoryType): CategoryConfig {
        if (type === CategoryType.SkillBasedGames) {
            return {
                type: CategoryType.SkillBasedGames,
                Kyc: this.kyc(),
                Sumsub: this.sumsub(),
                Withdraw: this.withdraw(),
                Currency: this.currency(),
                Legal: this.legal(),
            };
        }
        return {
            type: CategoryType.LuckyBasedGames,
            Kyc: this.kyc(),
            Sumsub: this.sumsub(),
            Currency: this.currency(),
            Legal: this.legal(),
        };
    }

    protected async save(): Promise<void> {
        if (this.mode() === 'add') {
            await this.create();
        } else {
            await this.update();
        }
    }

    private async create(): Promise<void> {
        this.clearErrors();

        const id = this.id().trim();
        const name = this.name().trim();
        let invalid = false;
        if (!id) {
            this.idError.set('ID is required');
            invalid = true;
        }
        if (!name) {
            this.nameError.set('Name is required');
            invalid = true;
        }
        if (invalid) return;

        const Config = this.buildConfig(this.type());

        this.saving.set(true);
        try {
            const { result } = await this.categoryService.addCategory({
                Id: id,
                Name: name,
                Config,
            });
            switch (result) {
                case 'Ok':
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Category created',
                        detail: name,
                    });
                    this.close();
                    break;
                case 'AlreadyExists':
                    this.idError.set('A category with this ID already exists');
                    break;
                case 'InvalidConfig':
                    this.generalError.set(
                        'The category configuration is invalid',
                    );
                    break;
                default:
                    this.generalError.set('Failed to create category');
            }
        } finally {
            this.saving.set(false);
        }
    }

    private async update(): Promise<void> {
        this.clearErrors();

        const target = this.editing();
        if (!target) return;

        const name = this.name().trim();
        if (!name) {
            this.nameError.set('Name is required');
            return;
        }

        // Type is immutable on edit — patch keeps the existing category's type.
        const existingType = target.Config.type;
        const patch: CategoryUpdatePatch = {
            Name: name,
            ConfigPatch:
                existingType === CategoryType.SkillBasedGames
                    ? {
                          type: CategoryType.SkillBasedGames,
                          Kyc: this.kyc(),
                          Sumsub: this.sumsub(),
                          Withdraw: this.withdraw(),
                          Currency: this.currency(),
                          Legal: this.legal(),
                      }
                    : {
                          type: CategoryType.LuckyBasedGames,
                          Kyc: this.kyc(),
                          Sumsub: this.sumsub(),
                          Currency: this.currency(),
                          Legal: this.legal(),
                      },
        };

        this.saving.set(true);
        try {
            const { result } = await this.categoryService.updateCategory({
                CategoryId: target.CategoryId,
                Patch: patch,
            });
            switch (result) {
                case 'Ok':
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Category updated',
                        detail: name,
                    });
                    this.close();
                    break;
                case 'NotFound':
                    this.generalError.set('Category no longer exists');
                    break;
                case 'TypeMismatch':
                    this.generalError.set(
                        'Category type mismatch — reload and try again',
                    );
                    break;
                default:
                    this.generalError.set('Failed to update category');
            }
        } finally {
            this.saving.set(false);
        }
    }

    protected trackById(_index: number, item: Category): string {
        return item.CategoryId;
    }
}

function cloneLegal(legal: CategoryLegalConfig): CategoryLegalConfig {
    return JSON.parse(JSON.stringify(legal));
}
